import apiService from '../services/api/apiService'
import { useCircuitBreaker } from '../hooks/useCircuitBreaker'
import { UserDetails } from '../interfaces'
import { accessTokenKey, NON_PROTECTED_ROUTES } from '../constants'
import { RootState } from '../rootReducer'
import { setAuth, setUserDetailsAndReadOnly } from '../features/login/loginSlice'
import React, { useEffect, useState, createContext, useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

interface AuthContext {
  isLoadingUserInfo: boolean
  userDetails?: UserDetails | undefined
}

const initialAuthContext: AuthContext = {
  isLoadingUserInfo: false
}

const AuthContext = createContext<AuthContext>(initialAuthContext)

const AuthProvider = ({ children }) => {
  const [userDetails, setUserDetails] = useState<UserDetails>()
  const [isLoading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const nonProtectedRoutes = useMemo(() => NON_PROTECTED_ROUTES.includes(pathname), [pathname])
  const isAuth = useSelector((state: RootState) => state.user.isAuth)

  const fetchUserData = async () => {
    const res = await apiService.getUserInfo()
    if (res) {
      dispatch(setUserDetailsAndReadOnly(res as UserDetails))
    }
    return res as UserDetails
  }

  const RETRY_LIMIT = 2
  const [fetchUserInfo_cb] = useCircuitBreaker<UserDetails>(
    RETRY_LIMIT,
    10000,
    fetchUserData,
    () => {
      throw new Error(`fetchUserInfo failed ${RETRY_LIMIT} times, aborting`)
    }
  )
  useEffect(() => {
    dispatch(setAuth(!!window.localStorage.getItem(accessTokenKey)))
  }, [])

  useEffect(() => {
    if (isAuth && !nonProtectedRoutes) {
      setLoading(true)
      fetchUserInfo_cb().then((res) => {
        res && setUserDetails(res)
        setLoading(false)
      })
    }
  }, [isAuth])

  return (
    <AuthContext.Provider value={{ isLoadingUserInfo: isLoading, userDetails }}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider

export const useAuth = () => useContext(AuthContext)
